import React, { useEffect, useMemo } from 'react';
import { Formik, Form } from 'formik';
import { Box, FormControlLabel, FormGroup, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Skeleton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BaseTextField from '../../../../theme/partials/BaseTextField';
import BaseButton from '../../../../theme/partials/BaseButton';
import { isEmpty } from '../../../../helpers/Functions';
import TextView from '../../../../theme/partials/Typography';
import { useCrudGetHelper } from '../../../crudHelpers/crudHelper';
import { createMessageFeedback, getFeedbackOptions, updateMessageFeedback } from '../../../crud/ai.crud';
import BaseCheckbox from '../../../../theme/partials/BaseCheckbox';
import { AI_FEEDBACLK_LIKE, AI_FEEDBACK_DISLIKE, ERROR_TYPE_AI_CHAT, AMPLITUDE_EVENT_FEEDBACK_CREATED_LIKE, AMPLITUDE_EVENT_FEEDBACK_CREATED_LIKE_DISLIKE } from '../../../../models/Constants';
import displayToast from '../../../../theme/partials/DisplayToast';
import useAmplitudeTracking from '../../../../helpers/AmplitudeHelper';

const OptionPlaceholder = () => {
	return (
		<Box>
			{[1, 2, 3, 4, 5].map((item) => (
				<Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
					<Skeleton variant="rectangular" width={20} height={20} sx={{ mr: 1.5 }} />
					<Skeleton variant="text" width="80%" height={24} />
				</Box>
			))}
		</Box>
	);
};

const AIFeedbackFormModal = ({
	show,
	onHide,
	isUpdate = false,
	messageId = null,
	feedbackType = AI_FEEDBACLK_LIKE,
	onStart = () => {},
	onSubmitSuccess = () => {},
	onFailure = () => {},
	onFinish = () => {},
}) => {
	const {
		initCall: initGetFeedbackOptions,
		responseData: feedbackOptions,
		isLoading: isLoadingFeedbackOptions,
	} = useCrudGetHelper(getFeedbackOptions);

	const { trackEventWithProperties, trackErrorEvent } = useAmplitudeTracking();

	// Handle both paginated ({ data: [...] }) and plain array responses
	const feedbackReasons = Array.isArray(feedbackOptions?.data)
		? feedbackOptions.data
		: feedbackOptions?.data?.data || [];
	const isDislike = feedbackType === AI_FEEDBACK_DISLIKE;

	const feedbackOptionsMap = useMemo(() => {
		return feedbackReasons.reduce((map, reason) => {
			map[reason.id] = reason;
			return map;
		}, {});
	}, [feedbackReasons]);

	useEffect(() => {
		if (isDislike) {
			initGetFeedbackOptions({});
		}
	}, [isDislike]);

	const getTitle = () => {
		return isDislike ? 'Help us improve—what was wrong with the answer? Please select all that apply.' : 'Tell us more.';
	};

	const getPlaceholder = () => {
		return isDislike 
			? 'Please provide more details about what was wrong with this response...'
			: 'Tell us what you liked about this response...';
	};

	return (
		<Dialog
			open={show}
			onClose={() => {}}
			disableEscapeKeyDown
			maxWidth="sm"
			fullWidth
			scroll="paper"
		>
			<DialogTitle>
				<TextView fontsize="medium" fontcolor="septenary">
					{getTitle()}
				</TextView>
				<IconButton
					aria-label="close"
					onClick={onHide}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<Formik
				enableReinitialize={true}
				initialValues={{
					feedback_type: feedbackType,
					message_id: messageId,
					option_ids: [],
					custom_feedback: '',
				}}
				validate={(values) => {
					const errors = {};
					if (isDislike && feedbackReasons.length > 0 && values.option_ids.length === 0) {
						errors.option_ids = 'Please select at least one reason';
					}
					return errors;
				}}
				onSubmit={(values, { setSubmitting }) => {
					onStart();
					const payload = isDislike 
						? values 
						: {
							feedback_type: values.feedback_type,
							message_id: values.message_id,
							custom_feedback: values.custom_feedback,
						};
					
					const amplitudePayload = { ...payload };
					amplitudePayload.option_texts = amplitudePayload.option_ids
						? amplitudePayload.option_ids.map((id) => feedbackOptionsMap[id]?.option_text || '').filter((text) => !isEmpty(text))
						: [];

					const feedbackMethod = isUpdate ? updateMessageFeedback : createMessageFeedback;

					feedbackMethod(payload)
						.then((response) => {
							if (response.data?.init?.[0]?.status === "error") {
								trackErrorEvent(ERROR_TYPE_AI_CHAT, response?.data?.init?.[0]?.message || 'Error submitting AI feedback');
								displayToast("error", response?.data?.init?.[0]);
								onFailure(response?.data?.init);
								return;
							}

							onSubmitSuccess(response.data?.data?.[0]);
							displayToast("success", `Feedback ${isUpdate ? 'updated' : 'submitted'} successfully.`);
							onHide();

							trackEventWithProperties(
								amplitudePayload.feedback_type === AI_FEEDBACLK_LIKE ? AMPLITUDE_EVENT_FEEDBACK_CREATED_LIKE : AMPLITUDE_EVENT_FEEDBACK_CREATED_LIKE_DISLIKE, 
								amplitudePayload
							);
						})
						.catch((error) => {
							trackErrorEvent(ERROR_TYPE_AI_CHAT, 'Error submitting AI feedback');
							onFailure(error);
						})
						.finally(() => {
							onFinish();
							setSubmitting(false);
						});
				}}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					handleSubmit,
					isSubmitting,
					setFieldValue,
				}) => (
					<Form onSubmit={handleSubmit}>
						<DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
							{isDislike && (
								<Box className="mb-3">
									<TextView fontcolor="septenary" fontsize="medium" className="mb-2">
										What went wrong?
									</TextView>
									<Box sx={{ px: 2 }}>
										{isLoadingFeedbackOptions ? (
											<OptionPlaceholder />
										) : (
											<FormGroup>
												{feedbackReasons.length > 0 ? (
													feedbackReasons.map((reason) => (
														<FormControlLabel
															key={reason.id}
															control={
																<BaseCheckbox
																	checked={values.option_ids.includes(reason.id)}
																	onChange={(e) => {
																		const isChecked = e.target.checked;
																		if (isChecked) {
																			setFieldValue('option_ids', [...values.option_ids, reason.id]);
																		} else {
																			setFieldValue(
																				'option_ids',
																				values.option_ids.filter((r) => r !== reason.id)
																			);
																		}
																	}}
																	name={reason.id}
																/>
															}
															label={reason.option_text}
														/>
													))
												) : (
													<TextView fontsize="secondary" fontcolor="septenary">
														No report options available. Please contact your administrator.
													</TextView>
												)}
											</FormGroup>
										)}
										{touched.option_ids && errors.option_ids && (
											<TextView fontsize="small" fontcolor="red" className="mt-1">
												{errors.option_ids}
											</TextView>
										)}
									</Box>
								</Box>
							)}

							<BaseTextField
								title="Additional Feedback (Optional)"
								name="custom_feedback"
								placeholder={getPlaceholder()}
								multiline
								minRows={4}
								maxRows={10}
								value={values.custom_feedback}
								onBlur={handleBlur}
								onChange={handleChange}
								helperText={touched.custom_feedback && errors.custom_feedback}
								error={Boolean(touched.custom_feedback && errors.custom_feedback)}
							/>
						</DialogContent>
						<DialogActions>
							{!isSubmitting && (
								<BaseButton variant="abort" onClick={onHide}>
									Cancel
								</BaseButton>
							)}
							<BaseButton type="submit" issubmitting={isSubmitting}>
								Submit
							</BaseButton>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default AIFeedbackFormModal;
